import { Bill } from '@features/bills/types/bill'
import { IntegrationStatus } from '@features/bills/types/integrationStatus'
import { getBillStatus } from '@features/bills/utils/getBillStatus'
import { BalanceModifier } from '@modules-deprecated/inbox/types'
import { getPaymentIdFromBalance } from '@modules-deprecated/inbox/utils'

import { getLocalDateFromDateString } from '../../../../../utils/getLocalDateFromDateString'
import { UseFetchBillsResponse } from '../../../routes/BillsList/revamped/hooks/useFetchBills'
import { getLatestIntegrationStatus } from '../../../utils/getLatestIntegrationStatus'
import { BillsTableRow } from '../types/billsTableRow'

const getSyncStatus = (bill: Bill): IntegrationStatus | null => {
  const latestIntegrationStatus = getLatestIntegrationStatus(bill.integrationStatus)

  // Sanity check: only trust the integration status if it matches the bill's latest sync status
  if (!latestIntegrationStatus || latestIntegrationStatus.status !== bill.latestCpaSyncStatus) {
    return null
  }

  return latestIntegrationStatus
}

/**
 * Utility function to handle formatting common API responses to the bills table data structure.
 */
export const formatFetchBillsResponseToBillsTableData = ({ bills }: UseFetchBillsResponse): BillsTableRow[] =>
  bills.map<BillsTableRow>((bill) => {
    const attachment = bill.attachments?.[0] as (typeof bill)['attachments'][number] | undefined

    const actualDueDate = getLocalDateFromDateString(bill.dueDate || undefined) ?? null
    const derivedStatus = getBillStatus({
      amount: bill.amount,
      balance: bill.balance,
      dueDate: actualDueDate || undefined,
      isPaid: bill.isPaid,
      state: bill.state,
      tax: bill.tax,
    })

    const safeBalanceModifiers = bill.balanceModifiers.filter(
      (modifier): modifier is BalanceModifier => modifier.isVoided !== null,
    )

    return {
      id: bill.id,
      status: bill.state,
      derivedStatus,
      syncStatus: getSyncStatus(bill),
      eInvoiceStatus: bill.pdpEInvoiceStatus,
      isEInvoice: Boolean(bill.isEInvoice),
      reference: {
        actual: bill.voucherNo,
        suggestion: null,
      },
      receiptDate: {
        actual: getLocalDateFromDateString(bill.entryDate || undefined) ?? null,
        suggestion: getLocalDateFromDateString(attachment?.documentDate) ?? null,
      },
      dueDate: {
        actual: actualDueDate,
        suggestion: null,
      },
      amountTotal: {
        actual: bill.balance,
        suggestion: attachment?.amount ?? null,
      },
      description: {
        actual: bill.lineDescription,
        suggestion: null,
      },
      supplier: {
        actual: bill.contact?.name ?? null,
        suggestion: attachment?.supplier ?? null,
      },
      type: bill.type,
      currency: {
        actual: bill.currencyId,
        suggestion: attachment?.currencyId ?? null,
      },
      hasAttachments: Boolean(attachment),
      accountingBasis: bill.accountingBasis,
      paymentId: getPaymentIdFromBalance(safeBalanceModifiers, bill.id) ?? null,
      bill,
    }
  })
